import React, { useEffect, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { useStatusStyle } from '@europrocurement/l2d-modules/hooks';
import { faBuildingColumns } from '@fortawesome/sharp-regular-svg-icons';
import { DOSSIERS_OPEN_SEARCH_SLICE_NAME } from '@europrocurement/l2d-domain';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { type DossierOpenSearchJsonldOpensearchDossierValideursStatusRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import DashboardStatCardTotalBar from '../molecules/DashboardStatCardTotalBar/DashboardStatCardTotalBar';
import { type DashboardFiltersType } from '../../containers/FormalistDashboard/FormalistDashboard';

type ValidatorsStatusWidgetDashboardProps = {
    formalistIds: number[];
    validatorsIds: number[];
    filters: DashboardFiltersType;
    listeDossiersPath: string;
    validatorLabel: string;
    validatorStatuses: DossierOpenSearchJsonldOpensearchDossierValideursStatusRead[];
    formalistName?: string;
};

type MergedGreffeStatus = {
    libelleStatus: string;
    statusFlag: number;
    childsCount: number;
    idStatuses: number[];
    sortOrder?: number | null;
};

const ValidatorsStatusWidgetDashboard: React.FunctionComponent<ValidatorsStatusWidgetDashboardProps> =
    function ({
        formalistIds,
        validatorsIds,
        filters,
        listeDossiersPath,
        formalistName,
        validatorStatuses,
        validatorLabel,
    }) {
        const { palette } = useTheme();
        const { getValidatorStatusStyle } = useStatusStyle();
        const dispatch = useDispatch();
        const navigate = useNavigate();

        const [mergedStatuses, setMergedStatuses] = useState<MergedGreffeStatus[]>([]);

        const handleValidatorStatusClick = (statusIds: number[]) => {
            if (statusIds.length === 0) return;

            dispatch({ type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/resetmainFilter` });
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                payload: { key: 'formalisteId', value: formalistIds },
            });
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                payload: { key: 'dateCreation', value: filters },
            });
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                payload: { key: 'statutValideurId', value: statusIds },
            });

            if (formalistName) {
                dispatch({
                    type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilterLabel`,
                    payload: { key: 'formalisteId', label: formalistName },
                });
            }

            navigate(`/${listeDossiersPath}`);
        };

        // Recap uses statusFlag as id — resolve back to all idStatuses for that flag
        const handleRecapItemClick = (statusFlag?: number | null) => {
            if (!statusFlag) return;
            const match = mergedStatuses.find((s) => s.statusFlag === statusFlag);
            if (match) handleValidatorStatusClick(match.idStatuses);
        };

        const filterAndMergeValidatorStatuses = async () => {
            const allStatuses = validatorStatuses
                .filter(
                    (status) =>
                        validatorsIds.includes(status.idValideur as number) &&
                        status.sortOrder != null &&
                        status.libelleStatus !== 'Validé',
                )
                .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

            // Merge by libelleStatus, summing childsCount
            const map = new Map<string, MergedGreffeStatus>();
            allStatuses.forEach((status) => {
                const key = status.libelleStatus ?? '';
                const existing = map.get(key);
                if (existing) {
                    existing.childsCount += (status.childsCount as number) || 0;
                    existing.idStatuses.push(status.idStatus as number);
                } else {
                    map.set(key, {
                        libelleStatus: key,
                        statusFlag: status.statusFlag as number,
                        childsCount: (status.childsCount as number) || 0,
                        idStatuses: [status.idStatus as number],
                        sortOrder: status.sortOrder,
                    });
                }
            });

            setMergedStatuses(
                Array.from(map.values()).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
            );
        };

        useEffect(() => {
            filterAndMergeValidatorStatuses();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formalistIds, filters.startDate, filters.endDate, validatorStatuses]);

        const recapBoxSx = {
            width: '100%',
            height: '100%',
            backgroundColor: palette.background.paper,
            borderRadius: 2,
            boxShadow: `0px 2px 2.5px 0px ${palette.background.shadow}`,
            display: 'flex',
            flexDirection: 'column',
            p: 2,
        };

        return (
            <Box sx={recapBoxSx}>
                <DashboardStatCardTotalBar
                    items={mergedStatuses.map((status) => ({
                        id: status.statusFlag,
                        libelle: status.libelleStatus,
                        childsCount: status.childsCount,
                        key: status.libelleStatus,
                    }))}
                    title={`Suivi ${validatorLabel} — flux actif`}
                    icon={faBuildingColumns}
                    handleItemClick={handleRecapItemClick}
                    handleStyleStatus={getValidatorStatusStyle}
                />
            </Box>
        );
    };

export default ValidatorsStatusWidgetDashboard;
